/**
 * The Overview analytics' decisions (A8, audits/2026-10-21/overview-analytics-spec.md ред.
 * 3), where a runner can reach them — the reason `campaigns.ts` gives for being a module
 * rather than a component. The figures come from `GET /api/v1/overview-stats` and from
 * nowhere else: the mirror the hourly cycle writes, never X.
 *
 * 🔴 **Three derived figures, each with its formula and its refusal.** CTR is
 * url_clicks ÷ impressions; CPM is billed ÷ impressions × 1000; CPC is billed ÷
 * url_clicks. Each is `null` — printed as «—» — when its denominator is 0 or null, or its
 * numerator is null: a ratio over nothing is not a figure, and a ratio over a figure
 * nobody read is a guess. CPM and CPC come out in micro-units of the account's currency
 * and are truncated to a whole micro here; turning them into an amount is money.ts's, as
 * every other money figure in this SPA.
 *
 * 🔴 **A partial read is said, never summed over silently.** The route's sum covers the
 * campaigns the cycle reached; `campaignsListed` is how many X listed. Fewer read than
 * listed is `partial`, for the week and for each day.
 */
import type {
	Campaign,
	OverviewCycle,
	OverviewDay,
	OverviewFigures,
	OverviewStatsAnswer,
} from "./api.ts";

/** Click-through rate as a fraction: url_clicks ÷ impressions, or null. */
export function ctr(f: Pick<OverviewFigures, "urlClicks" | "impressions">): number | null {
	if (f.urlClicks === null || f.impressions === null || f.impressions === 0) return null;
	return f.urlClicks / f.impressions;
}

/** Cost per thousand impressions, in whole micro-units: billed ÷ impressions × 1000. */
export function cpmMicro(f: Pick<OverviewFigures, "billedMicro" | "impressions">): number | null {
	if (f.billedMicro === null || f.impressions === null || f.impressions === 0) return null;
	return Math.trunc((f.billedMicro * 1000) / f.impressions);
}

/** Cost per URL click, in whole micro-units: billed ÷ url_clicks. */
export function cpcMicro(f: Pick<OverviewFigures, "billedMicro" | "urlClicks">): number | null {
	if (f.billedMicro === null || f.urlClicks === null || f.urlClicks === 0) return null;
	return Math.trunc(f.billedMicro / f.urlClicks);
}

/** How many campaigns the sum covers, against how many X listed — or null before any cycle. */
export function coverage(
	answer: Pick<OverviewStatsAnswer, "campaigns" | "campaignsListed">,
): { read: number; listed: number; partial: boolean } | null {
	if (answer.campaignsListed === null) return null;
	const read = answer.campaigns.length;
	return { read, listed: answer.campaignsListed, partial: read < answer.campaignsListed };
}

/** Whether a day's sum covers fewer campaigns than X listed. */
export function dayPartial(day: Pick<OverviewDay, "campaigns">, listed: number | null): boolean {
	return listed !== null && day.campaigns < listed;
}

/** The metrics a day's bar can show — the four the screen names. */
export type BarMetric = "billedMicro" | "impressions" | "engagements" | "urlClicks";

/**
 * One bar per day, its height as a percentage of the tallest. 🔴 A day whose figure is
 * null has no bar (`height: null`) — not a bar of zero — so «not read» and «read, nothing»
 * stay apart in the picture as they do in the numbers.
 */
export function bars(
	days: readonly OverviewDay[],
	metric: BarMetric,
): { day: string; value: number | null; height: number | null }[] {
	const max = days.reduce((m, d) => Math.max(m, d[metric] ?? 0), 0);
	return days.map((d) => {
		const value = d[metric];
		return {
			day: d.day,
			value,
			height: value === null ? null : max === 0 ? 0 : (value / max) * 100,
		};
	});
}

/** What the screen can say about one account's figures, before any number. */
export type OverviewStatsState =
	| { kind: "loading" }
	| { kind: "failed"; why: string }
	| { kind: "unavailable"; why: string }
	| { kind: "not-read-yet"; answer: OverviewStatsAnswer }
	| { kind: "ready"; answer: OverviewStatsAnswer };

/**
 * The state, in order: the read's own fate first, then a table the database does not have
 * (migration 0010), then a mirror no cycle has written for this account, then figures.
 */
export function overviewStatsState(
	read:
		| { state: "loading" }
		| { state: "failed"; why: string }
		| { state: "ready"; answer: OverviewStatsAnswer },
): OverviewStatsState {
	if (read.state === "loading") return { kind: "loading" };
	if (read.state === "failed") return { kind: "failed", why: read.why };
	const a = read.answer;
	if (a.unavailable !== undefined) return { kind: "unavailable", why: a.unavailable };
	if (a.days.length === 0 && a.today === null) return { kind: "not-read-yet", answer: a };
	return { kind: "ready", answer: a };
}

/** Why the last cycle did not finish, in its own words — or null when it did. */
export function cycleUnfinished(cycle: OverviewCycle | null): string | null {
	if (cycle === null) return null;
	if (cycle.skipped !== undefined) return cycle.skipped;
	if (cycle.error !== undefined) return cycle.error;
	return null;
}

/**
 * Whether a campaign's row is X's «no serving»: the cycle read it, and X reported no
 * impressions and nothing billed over the days answered. A row of zeros the route made
 * from X's nulls is exactly this (contract, «The null rule»).
 */
export function noServing(f: Pick<OverviewFigures, "impressions" | "billedMicro">): boolean {
	return f.impressions === 0 && f.billedMicro === 0;
}

/** The account's campaign list as the Overview read it, for names only. */
export type NamesRead =
	| { state: "loading" }
	| { state: "failed"; why: string }
	| { state: "ready"; campaigns: readonly Pick<Campaign, "id" | "name">[]; truncated: boolean };

/**
 * A campaign's name, from one `GET /api/v1/campaigns` per account (X's Core Entity reads —
 * a different budget from Analytics; the lead's decision, 2026-10-22), matched by id here.
 * 🔴 Four answers and no guess: the name; still reading; the read failed; the id is not on
 * the page read (X has more than one page, or the campaign left the list).
 */
export type CampaignName =
	| { kind: "name"; name: string }
	| { kind: "reading" }
	| { kind: "failed" }
	| { kind: "not-on-page"; truncated: boolean };

export function campaignName(id: string, read: NamesRead): CampaignName {
	if (read.state === "loading") return { kind: "reading" };
	if (read.state === "failed") return { kind: "failed" };
	const row = read.campaigns.find((c) => c.id === id);
	return row
		? { kind: "name", name: row.name }
		: { kind: "not-on-page", truncated: read.truncated };
}
